import { Injectable, inject, signal } from '@angular/core';
import { LoginResponse } from '../interfaces';
import { CookieService } from './cookie.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly cookieService = inject(CookieService);

  private readonly ACCESS_TOKEN_KEY = 'eat';
  private readonly USER_KEY = 'user_data';
  private readonly COOKIE_EXPIRY_DAYS = 7;

  currentUser = signal<LoginResponse | null>(this.loadUser());

  setSession(response: LoginResponse): void {
    this.cookieService.set(this.ACCESS_TOKEN_KEY, response.token, this.cookieOptions());
    this.cookieService.set(this.USER_KEY, JSON.stringify(response), this.cookieOptions());
    this.currentUser.set(response);
  }

  /** Refreshes the stored user details (e.g. from /auth/me) without altering the token. */
  setUser(user: LoginResponse): void {
    this.cookieService.set(this.USER_KEY, JSON.stringify(user), this.cookieOptions());
    this.currentUser.set(user);
  }

  clearSession(): void {
    this.cookieService.delete(this.ACCESS_TOKEN_KEY);
    this.cookieService.delete(this.USER_KEY);
    this.currentUser.set(null);
  }

  getAccessToken(): string | null {
    return this.cookieService.get(this.ACCESS_TOKEN_KEY);
  }

  getUserRoles(): string[] {
    return this.currentUser()?.roles ?? [];
  }

  getUserTenantIds(): number[] {
    return this.currentUser()?.tenant_ids ?? [];
  }

  getUserTasks(): string[] {
    return [];
  }

  private cookieOptions() {
    return {
      expiresDays: this.COOKIE_EXPIRY_DAYS,
      sameSite: 'Strict' as const,
      // Secure cookies are dropped on http; only enable on deployed (https) builds.
      secure: environment.production || environment.staging,
    };
  }

  private loadUser(): LoginResponse | null {
    try {
      const stored = this.cookieService.get(this.USER_KEY);
      return stored ? (JSON.parse(stored) as LoginResponse) : null;
    } catch {
      return null;
    }
  }
}
